import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Boxes, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useRegisterStockMovement, useStockMovements } from '@/hooks/useStock';
import { useRole } from '@/hooks/useRole';
import {
  MANUAL_STOCK_REASONS,
  movementDelta,
  adjustmentDelta,
  formatStockQuantity,
  reasonLabel,
} from '@/lib/stock';
import { variantDisplayName } from '@/lib/variants';
import type { StockReason } from '@/types/database';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tracked product whose stock we're moving. */
  product: {
    id: string;
    name: string;
    unit?: string | null;
    stock_quantity: number;
    /** Preenchido quando o produto é uma variação (o saldo é dela, não do pai). */
    variant_name?: string | null;
  } | null;
}

const formatDateTime = (d?: string | null) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(d);
  }
};

/**
 * Registra um movimento manual de estoque (entrada/saída/compra/ajuste) para um
 * produto rastreado e mostra o histórico. O saldo é decidido pelo servidor (RPC).
 */
export function StockMovementModal({ open, onOpenChange, product }: Props) {
  const register = useRegisterStockMovement();
  const { canWrite } = useRole();
  const { movements } = useStockMovements(product?.id);

  const [reason, setReason] = useState<StockReason>('manual_in');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  const direction = MANUAL_STOCK_REASONS.find((r) => r.value === reason)?.direction ?? 'in';
  const current = Number(product?.stock_quantity ?? 0);

  // Reset the form whenever the modal opens for a (possibly new) product.
  useEffect(() => {
    if (open) {
      setReason('manual_in');
      setQuantity('');
      setNote('');
    }
  }, [open, product?.id]);

  if (!product) return null;

  const handleSubmit = async () => {
    if (!canWrite) {
      toast.info('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    const magnitude = Number(quantity.replace(',', '.'));
    if (!Number.isFinite(magnitude) || magnitude < 0) {
      toast.error('Informe uma quantidade válida.');
      return;
    }

    const delta =
      direction === 'set' ? adjustmentDelta(current, magnitude) : movementDelta(reason, magnitude);

    if (delta === 0) {
      toast.info(direction === 'set' ? 'A quantidade contada é igual ao saldo atual.' : 'Informe uma quantidade.');
      return;
    }

    try {
      const res = await register.mutateAsync({
        productId: product.id,
        delta,
        reason,
        note: note.trim() || null,
      });
      if ((res as any).offline) {
        toast.success('Movimento salvo localmente! Será sincronizado quando houver internet.', { icon: '📴' });
      } else {
        toast.success(`Estoque atualizado: ${formatStockQuantity((res as any).balance)}.`);
      }
      onOpenChange(false);
    } catch {
      // toast handled in the mutation
    }
  };

  const unit = product.unit ? String(product.unit) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {/* Variação: mostra "Camiseta — P" para não haver dúvida de qual
              unidade está sendo movimentada. */}
          <DialogTitle className="truncate">
            {variantDisplayName(product.name, product.variant_name)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Saldo atual */}
          <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-3">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Boxes className="h-4 w-4" /> Saldo atual
            </span>
            <span className={`text-lg font-semibold ${current < 0 ? 'text-destructive' : ''}`}>
              {formatStockQuantity(current)} {unit}
            </span>
          </div>

          {/* Tipo de movimento */}
          <div className="space-y-2">
            <Label>Movimento</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as StockReason)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_STOCK_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantidade / nova contagem */}
          <div className="space-y-2">
            <Label htmlFor="stock-qty">
              {direction === 'set' ? 'Nova quantidade contada' : 'Quantidade'}
            </Label>
            <Input
              id="stock-qty"
              inputMode="decimal"
              autoFocus
              placeholder="0"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {direction === 'set'
                ? 'O saldo será ajustado para este valor (correção de contagem).'
                : direction === 'out'
                  ? 'Será subtraído do saldo. O saldo pode ficar negativo.'
                  : 'Será somado ao saldo.'}
            </p>
          </div>

          {/* Observação */}
          <div className="space-y-2">
            <Label htmlFor="stock-note">Observação (opcional)</Label>
            <Input
              id="stock-note"
              placeholder="Ex: NF 1234, ajuste de inventário…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          {/* Histórico */}
          {movements.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Histórico</Label>
              <div className="max-h-52 space-y-1.5 overflow-y-auto">
                {movements.slice(0, 30).map((m: any) => {
                  const isIn = Number(m.delta) >= 0;
                  return (
                    <div key={m.id} className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm">
                      <div className="flex min-w-0 items-center gap-2">
                        {isIn ? (
                          <ArrowUpCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                        ) : (
                          <ArrowDownCircle className="h-4 w-4 shrink-0 text-destructive" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-xs">{reasonLabel(m.reason)}</p>
                          <p className="text-[11px] text-muted-foreground">{formatDateTime(m.created_at)}</p>
                        </div>
                        {m._offline_pending && (
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            Offline
                          </Badge>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className={`font-medium ${isIn ? 'text-emerald-600' : 'text-destructive'}`}>
                          {isIn ? '+' : ''}
                          {formatStockQuantity(Number(m.delta))}
                        </p>
                        {m.balance_after != null && (
                          <p className="text-[11px] text-muted-foreground">
                            = {formatStockQuantity(Number(m.balance_after))}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button3D onClick={handleSubmit} disabled={register.isPending || !canWrite} className="w-full sm:w-auto">
            {register.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Registrar movimento
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
